import { BellRingIcon, HeartHandshakeIcon, UsersIcon, WalletCardsIcon } from 'lucide-react'
import type { ProductPageContent } from '../../types/product'
import { industryCatalog, productCatalog } from './shared'

export const customers: ProductPageContent = {
  slug: 'customers',
  name: 'Customers',
  icon: UsersIcon,
  hero: {
    headline: 'Know your customers and get paid what you’re owed',
    subheadline:
      'Keep every customer’s history, balances, and credit in one place, send friendly payment reminders, and give your regulars a reason to come back.',
    trustLine: 'Balances and credit always up to date · Used by 5,100+ businesses',
    visual: {
      kind: 'devices',
      tablet: {
        module: 'Customers',
        greeting: 'Customers · 1,842 contacts',
        kpis: [
          { label: 'Owed to you', value: 'RWF 2.8M', delta: { label: '12 overdue', tone: 'warning' } },
          { label: 'Repeat customers', value: '64%', delta: { label: '+5% this quarter', tone: 'positive' } },
          { label: 'Collected this week', value: 'RWF 940K' },
        ],
        main: {
          type: 'list',
          title: 'Credit balances',
          rows: [
            { label: 'Ineza Provisions', sublabel: 'Due 15 Sep', value: 'RWF 128,000', tone: 'negative' },
            { label: 'Hotel Muhabura', sublabel: 'Due 30 Sep', value: 'RWF 342,500' },
            { label: 'Byumba Retail', sublabel: 'Due 5 Oct', value: 'RWF 42,500' },
          ],
        },
        side: {
          type: 'progress',
          title: 'Top customers',
          rows: [
            { label: 'Hotel Muhabura', value: 'RWF 4.2M', pct: 100 },
            { label: 'Ineza Provisions', value: 'RWF 2.9M', pct: 70 },
            { label: 'Café Rubavu', value: 'RWF 1.6M', pct: 38 },
          ],
        },
      },
      phone: {
        type: 'list',
        title: 'Payment reminder sent',
        meta: 'Ineza Provisions · 078 •••• 552',
        rows: [
          { label: 'Balance', value: 'RWF 128,000', tone: 'negative' },
          { label: 'Overdue by', value: '3 days' },
        ],
        action: 'Record payment',
      },
    },
  },
  stats: [
    { value: '5,100+', label: 'businesses managing customers with Kayko' },
    { value: 'RWF 900M+', label: 'in customer credit tracked every month' },
    { value: '2×', label: 'faster collection of overdue balances on average' },
  ],
  benefits: {
    heading: 'Build relationships that last.',
    items: [
      { icon: HeartHandshakeIcon, title: 'Full customer history', copy: 'See every purchase, payment, and note for each customer before they reach the counter.' },
      { icon: WalletCardsIcon, title: 'Credit under control', copy: 'Set credit limits, track balances, and know exactly who owes what.' },
      { icon: BellRingIcon, title: 'Friendly reminders', copy: 'Send polite SMS reminders before and after payments are due.' },
    ],
  },
  features: {
    heading: 'Everything you need to look after customers.',
    subheading: 'For shops, wholesalers, hotels, and service businesses that sell on credit.',
    items: [
      {
        title: 'A profile for every customer',
        copy: 'Save contact details, purchase history, and notes for each customer. Your team can see who they’re serving and what they usually buy.',
        bullets: ['Phone, TIN, and delivery address on file', 'Complete purchase and payment history', 'Notes and tags for special requests'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'Hotel Muhabura',
            meta: 'Musanze · Customer since 2023',
            badge: { label: 'Wholesale', tone: 'brand' },
            rows: [
              { label: 'Total purchases', value: 'RWF 4,210,000' },
              { label: 'Last order', sublabel: '12 Sep · 14 items', value: 'RWF 186,000' },
              { label: 'Usual order', value: 'Rice, oil, tea' },
              { label: 'Credit limit', value: 'RWF 500,000' },
            ],
          },
        },
      },
      {
        title: 'Customer credit and balances',
        copy: 'Sell on credit with confidence. Kayko tracks every balance, warns you when a customer is near their limit, and shows what’s overdue.',
        bullets: ['Credit limits per customer', 'Balances update with every sale and payment', 'Overdue list sorted by amount and age'],
        visual: {
          kind: 'card',
          card: {
            type: 'progress',
            title: 'Credit used',
            badge: { label: '3 near limit', tone: 'warning' },
            rows: [
              { label: 'Ineza Provisions', value: 'RWF 128K / 150K', pct: 85, tone: 'warning' },
              { label: 'Hotel Muhabura', value: 'RWF 342K / 500K', pct: 68 },
              { label: 'Café Rubavu', value: 'RWF 95K / 100K', pct: 95, tone: 'negative' },
              { label: 'Byumba Retail', value: 'RWF 42K / 200K', pct: 21 },
            ],
          },
        },
      },
      {
        title: 'Payment reminders by SMS',
        copy: 'Send reminders automatically before a payment is due and again if it’s late. Customers can pay by mobile money and balances update on their own.',
        bullets: ['Automatic or one-tap reminders', 'Messages in Kinyarwanda, English, or French', 'Statement link included in every reminder'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'Reminders this week',
            rows: [
              { label: 'Ineza Provisions', sublabel: 'Sent Mon · Paid Tue', value: 'Paid', tone: 'positive' },
              { label: 'Café Rubavu', sublabel: 'Sent Wed', value: 'RWF 95,000', tone: 'warning' },
              { label: 'Kabeza Salon', sublabel: 'Scheduled Fri', value: 'RWF 18,000' },
            ],
            footer: { label: 'Collected after reminders', value: 'RWF 412,000' },
          },
        },
      },
      {
        title: 'Loyalty that brings people back',
        copy: 'Reward regular customers with points or discounts and see who hasn’t visited in a while. Small gestures keep your best customers close.',
        bullets: ['Points or discounts on repeat purchases', 'Lists of customers who haven’t returned', 'Birthday and seasonal SMS offers'],
        visual: {
          kind: 'card',
          card: {
            type: 'bars',
            title: 'Repeat customers',
            value: '64%',
            delta: { label: '+5% this quarter', tone: 'positive' },
            bars: [
              { label: 'Apr', value: 52 },
              { label: 'May', value: 55 },
              { label: 'Jun', value: 57 },
              { label: 'Jul', value: 59 },
              { label: 'Aug', value: 61 },
              { label: 'Sep', value: 64, highlight: true },
            ],
            legend: '218 customers earned rewards in September',
          },
        },
      },
    ],
  },
  steps: {
    heading: 'Up and running in an afternoon.',
    items: [
      { title: 'Import your contacts', copy: 'Upload your customer list or add customers as you sell.' },
      { title: 'Set credit terms', copy: 'Choose who can buy on credit, their limits, and payment terms.' },
      { title: 'Let Kayko follow up', copy: 'Balances stay current and reminders go out on schedule.' },
    ],
  },
  related: {
    heading: 'Works with the rest of Kayko.',
    subheading: 'Customer records connect to every sale and report.',
    items: [productCatalog['sales-pos'], productCatalog.reports, productCatalog.inventory, productCatalog['business-finance']],
  },
  industries: {
    heading: 'Built for your industry.',
    subheading: 'Customers adapts to regulars, guests, patients, and clients.',
    items: [industryCatalog.retail, industryCatalog.services, industryCatalog.hotels, industryCatalog.healthcare],
  },
  testimonial: {
    quote:
      'Our regulars love that we remember their order. With Kayko I also know who hasn’t been in for a while, and a small offer brings them straight back.',
    name: 'Héritier Muhire',
    role: 'Founder',
    business: 'YEET Coffee',
    location: 'Kacyiru, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/c04828fc-7f33-4022-bb39-af6bdb618150.jpg',
    metric: { value: '+22%', label: 'more repeat visits after launching rewards' },
  },
  pricingTeaser: { text: 'Customers is included in every Kayko plan.', linkLabel: 'See pricing', href: '#' },
  faq: {
    heading: 'Frequently asked questions',
    items: [
      { question: 'Can I sell on credit to some customers only?', answer: 'Yes. Credit is off by default and you choose which customers can use it, with a limit for each.' },
      { question: 'How do SMS reminders work?', answer: 'Kayko sends a short message with the balance and a statement link. You choose the timing and wording, and can turn reminders off per customer.' },
      { question: 'Can customers pay balances by mobile money?', answer: 'Yes. Record mobile money payments against the balance, and it updates straight away.' },
      { question: 'Is my customer data private?', answer: 'Yes. Your customer list belongs to you. It is stored securely and never shared or used for marketing by Kayko.' },
      { question: 'Can I send statements?', answer: 'Yes. Send a statement by SMS or email, or download it as a PDF for any date range.' },
      { question: 'Can I import my existing customers?', answer: 'Yes. Upload a spreadsheet with names, phone numbers, and opening balances.' },
    ],
  },
  cta: {
    headline: 'Keep every customer close.',
    subheadline: 'Import your contacts in minutes, with support from our team in Kigali.',
  },
}
